import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { reports } from "../api/endpoints";
import { apiBase } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import Spinner from "../components/Spinner";
import { TipoPill, SlaPill, slaState } from "../components/Pills";
import CaseDetail from "./case-detail";
import { useHoverIntent } from "../hooks/useHoverIntent";
import { HoverFocusProvider, useHoverFocus } from "../context/HoverFocusContext";
import { rolesOf } from "../utils/permissions";

const TIPOS = ["tutela", "fallo", "requerimiento", "desacato", "sancion"];

function greet(name) {
  const h = new Date().getHours();
  if (h < 12) return `Buenos días, ${name}`;
  if (h < 19) return `Buenas tardes, ${name}`;
  return `Buenas noches, ${name}`;
}
function todayLabel() {
  return new Date().toLocaleDateString("es-CO", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}
function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

export default function Dashboard() {
  return (
    <HoverFocusProvider>
      <DashboardInner />
    </HoverFocusProvider>
  );
}

function DashboardInner() {
  const { user } = useAuth();
  const toast = useToast();
  const { focusedRowId } = useHoverFocus();
  const [counters, setCounters] = useState(null);
  const [activity, setActivity] = useState(null);
  const [urgent, setUrgent] = useState(null);
  const [openId, setOpenId] = useState(null);
  // feature/notifications-clear — pagination on the activity log.
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const PAGE = 25;

  function load() {
    reports.dailyCounters().then(r => setCounters(r.data.counters)).catch(() => setCounters({}));
    reports.recentActivity(PAGE).then(r => {
      setActivity(r.data);
      setHasMore((r.data || []).length === PAGE);
    }).catch(() => setActivity([]));
    reports.urgentCases().then(r => setUrgent(r.data.filter(c => {
      const s = slaState(c); return s && (s.kind === "warn" || s.kind === "bad");
    }))).catch(() => setUrgent([]));
  }

  async function loadMoreActivity() {
    if (loadingMore || !hasMore || !activity?.length) return;
    setLoadingMore(true);
    try {
      const oldest = activity[activity.length - 1].created_at;
      const r = await reports.recentActivity(PAGE, oldest);
      setActivity(prev => [...(prev || []), ...(r.data || [])]);
      setHasMore((r.data || []).length === PAGE);
    } catch {
      // silent — toast would be noise on a load-more failure
    } finally { setLoadingMore(false); }
  }
  useEffect(() => { load(); }, []);

  // SLA pills update live every 60s
  useEffect(() => {
    const t = setInterval(() => setUrgent(u => u && [...u]), 60_000);
    return () => clearInterval(t);
  }, []);

  const firstName = (user.name || "").split(" ")[0];

  return (
    <div className="col" style={{ gap: 24 }}>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: 0 }}>{greet(firstName)}</h1>
          <div className="muted" style={{ textTransform: "capitalize" }}>{todayLabel()}</div>
        </div>
        <div style={{ flex: 1 }} />
        {/* feature/single-role-audit — multi-role aware admin gate. */}
        {rolesOf(user).includes("admin") && (
          <Link to="/admin/exports">
            <button className="secondary">Exportar LITISOFT del mes</button>
          </Link>
        )}
        {/* feature/reparto-excel-export, hardened Sprint 55.
            Originally wrapped in an <a href> + e.preventDefault() handler;
            some browsers followed the href anyway (no Authorization header
            → 401, mystery "broken" download), and the fetch flow never
            checked r.ok so a 401 saved as a JSON-blob renamed .xlsx.
            Now: bare <button>, fetch via apiBase, explicit r.ok check,
            .catch surfacing the error, save only on real success. */}
        {/* feature/single-role-audit — multi-role aware. */}
        {["reparto","admin","directora"].some(r => rolesOf(user).includes(r)) && (
          <button type="button" className="secondary" style={{ marginLeft: 8 }}
                  onClick={async () => {
                    const token = localStorage.getItem("eds.token");
                    try {
                      const r = await fetch(`${apiBase}/api/exports/reparto`, {
                        headers: { Authorization: "Bearer " + token },
                      });
                      if (!r.ok) {
                        let msg = `HTTP ${r.status}`;
                        try { msg = (await r.json()).error || msg; } catch (_) {}
                        throw new Error(msg);
                      }
                      const b = await r.blob();
                      const url = URL.createObjectURL(b);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `reparto-${new Date().toISOString().split("T")[0]}.xlsx`;
                      document.body.appendChild(a); a.click();
                      a.remove(); URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.push("No se pudo descargar el Excel: " + (err?.message || "error"), "error");
                    }
                  }}>
            ⬇ Descargar Excel reparto
          </button>
        )}
      </div>

      {/* Daily counters strip */}
      <div className="grid-5" style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 16 }}>
        {TIPOS.map(t => {
          const c = counters?.[t] || { today: 0, yesterday: 0 };
          const delta = c.today - c.yesterday;
          const up = delta > 0, flat = delta === 0;
          return (
            <div className="card tight stat-card" key={t}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <TipoPill tipo={t} />
                <span style={{ fontSize: 11, color: up ? "var(--accent-mint)" : flat ? "var(--text-hint)" : "var(--text-muted)", fontWeight: 500 }}>
                  {flat ? "—" : (up ? "↑" : "↓")} {flat ? "" : Math.abs(delta)}
                </span>
              </div>
              <div style={{ fontSize: 32, fontWeight: 600, color: "var(--text-primary)", margin: "8px 0 0" }}>
                {counters == null ? <Spinner /> : c.today}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Ayer: {c.yesterday}
              </div>
            </div>
          );
        })}
      </div>

      {/* Casos urgentes */}
      <div className="grid-2">
        <div className="card dashboard-card">
          <h2 style={{ marginTop: 0 }}>Casos urgentes</h2>
          <div className="rule" />
          {urgent == null ? <Spinner /> :
           !urgent.length ? <p className="muted">Sin casos en alerta.</p> : (
            <table>
              <thead><tr><th>Caso</th><th>Accionante</th><th>Tipo de actuación</th><th>SLA</th></tr></thead>
              <tbody className={"eds-focus-list" + (focusedRowId ? " has-focused-row" : "")}>
                {urgent.slice(0, 8).map(c => (
                  <UrgentRow key={c.id} c={c} onOpen={() => setOpenId(c.id)} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card dashboard-card">
          <h2 style={{ marginTop: 0 }}>Actividad reciente</h2>
          <div className="rule" />
          {activity == null ? <Spinner /> :
           !activity.length ? <p className="muted">Sin actividad todavía.</p> : (
            <div>
              {activity.map(e => (
                <div key={e.id} className="activity-item" onClick={() => e.case_id && setOpenId(e.case_id)}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--text-muted)", marginTop: 8, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div className="activity-message">
                      <span className="activity-actor">{e.actor_name || "Sistema"}</span>{" "}
                      <span style={{ color: "var(--text-muted)" }}>{actionLabel(e.action)}</span>{" "}
                      <span style={{ color: "var(--accent-mint)" }}>{e.case_number}</span>
                      {e.accionante_nombre && <span className="muted"> · {e.accionante_nombre}</span>}
                    </div>
                    <div className="activity-timestamp">{timeAgo(e.created_at)}</div>
                  </div>
                </div>
              ))}
              {hasMore && (
                <button
                  className="activity-load-more"
                  onClick={loadMoreActivity}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Cargando…" : "Cargar más actividad"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <CaseDetail caseId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

function UrgentRow({ c, onOpen }) {
  const { setFocusedRowId } = useHoverFocus();
  const intent = useHoverIntent(300, 100);
  useEffect(() => {
    if (intent.isFocused) setFocusedRowId(c.id);
    else setFocusedRowId(prev => (prev === c.id ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.isFocused, c.id]);
  return (
    <tr
      onClick={onOpen}
      onMouseEnter={intent.onMouseEnter}
      onMouseLeave={intent.onMouseLeave}
      className={"eds-focus-row" + (intent.isFocused ? " is-focused" : "")}
      style={{ cursor: "pointer" }}
    >
      <td>{c.case_number}</td>
      <td>{c.accionante_nombre || "—"}</td>
      <td><TipoPill tipo={c.tipo} /></td>
      <td><SlaPill c={c} /></td>
    </tr>
  );
}

function actionLabel(a) {
  return ({
    "case.create":           "creó",
    "case.update":           "editó",
    "case.delete":           "eliminó",
    "case.reassign":         "reasignó",
    "case.flag_error":       "marcó como creado por error",
    "case.field_edited":     "editó un campo de",
    "case.soft_deleted":     "eliminó",
    "case.devuelto_a_reparto":     "marcó devolución en",
    "case.devuelto_invalido_insumo":"marcó insumos como inválidos en",
    "case.devuelto_resuelto":      "resolvió la devolución de",
    "case.hard_deleted_by_admin":  "eliminó permanentemente",
    "case.insumo_screenshot_replaced": "reemplazó la captura del insumo en",
    "send_audit":            "envió a auditoría",
    "approve":               "aprobó",
    "reject":                "devolvió con observaciones",
    "claim_insumo":          "tomó insumo de",
    "mark_solicitud_status": "actualizó estado del insumo en",
    "upload_response":       "subió respuesta para",
    "decide_impugnar":       "decidió impugnación en",
    "mark_litisoft":         "marcó en LITISOFT",
    "close":                 "cerró",
    "insumo.pdf_generated":  "generó solicitud PDF para",
    "insumo.expediente_letter":"generó carta al juzgado para",
    "insumo.response_uploaded":"subió respuesta a",
  })[a] || a;
}
